import { useCallback, useState } from 'react'
import { SlackClient, type AuthResult } from '@assembly/slack'
import { usePlatform, type Platform } from './platform'
import { useAccounts, type Account } from './state/accounts'
import { errorMessage } from './data/actions'

/** Verifies a token and builds an Account from it. */
export async function accountFromAuth(result: AuthResult, platform: Platform): Promise<Account> {
  const client = new SlackClient(result.access_token, platform.transport)
  const test = await client.auth.test()
  const [team, user] = await Promise.all([client.team.info().catch(() => null), client.users.info(test.user_id).catch(() => null)])
  return {
    id: `${test.team_id}:${test.user_id}`,
    token: result.access_token,
    teamId: test.team_id,
    teamName: team?.name ?? test.team,
    teamDomain: team?.domain,
    teamIcon: team?.icon && !team.icon.image_default ? (team.icon.image_88 ?? team.icon.image_68) : undefined,
    userId: test.user_id,
    userName: user?.profile?.display_name || user?.real_name || user?.name,
  }
}

export function useSignIn() {
  const platform = usePlatform()
  const add = useAccounts((s) => s.add)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const complete = useCallback(
    async (result: AuthResult) => {
      const account = await accountFromAuth(result, platform)
      add(account)
      return account
    },
    [platform, add],
  )

  const start = useCallback(async () => {
    setError(null)
    setBusy(true)
    try {
      const result = await platform.signIn()
      if (result) await complete(result)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [platform, complete])

  const withToken = useCallback(
    async (token: string) => {
      setError(null)
      const t = token.trim()
      if (!t.startsWith('xoxp-')) {
        setError('Paste a Slack user token (it starts with “xoxp-”).')
        return
      }
      setBusy(true)
      try {
        await complete({ access_token: t })
      } catch (err) {
        setError(errorMessage(err))
      } finally {
        setBusy(false)
      }
    },
    [complete],
  )

  return { start, withToken, complete, busy, error, setError }
}
